'use client'

import { useEffect, useState } from 'react'
import { ExternalLink, Radio } from 'lucide-react'
import {
  parseStreamUrl,
  streamEmbedUrl,
  streamWatchUrl,
  streamPlatformLabel,
} from '@/lib/stream-embed'
import type { Tournament } from '@/lib/tournaments'

/**
 * The organizer's livestream, shown while the event is live.
 *
 * Embeds the player where we can and always offers the link out alongside —
 * Twitch refuses to embed if the `parent` domain doesn't match the host, and a
 * YouTube @handle has no embeddable form at all, so the link is the thing that
 * always works.
 */
export function StreamPlayer({ t }: { t: Tournament }) {
  // Twitch needs the embedding domain up front, and it must be the real host
  // (localhost in dev, gamerplug.app in prod) — reading it from the browser
  // keeps every environment working without a hardcoded list.
  const [host, setHost] = useState<string | null>(null)
  useEffect(() => {
    setHost(window.location.hostname)
  }, [])

  const raw = t.streamUrl?.trim()
  if (!raw) return null

  const parsed = parseStreamUrl(raw)
  const watchUrl = streamWatchUrl(parsed, raw)
  const embedUrl = host ? streamEmbedUrl(parsed, host) : null
  const label = parsed ? streamPlatformLabel(parsed.platform) : 'stream'

  return (
    <section className="mb-8">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <span className="relative flex size-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
            <span className="relative inline-flex size-2 rounded-full bg-red-500" />
          </span>
          Live now
        </h2>
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          Watch on {label}
          <ExternalLink className="size-3.5" />
        </a>
      </div>

      {embedUrl ? (
        <div className="overflow-hidden rounded-2xl border border-border bg-black">
          <div className="relative aspect-video w-full">
            <iframe
              src={embedUrl}
              title={`${t.title} livestream`}
              allowFullScreen
              allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
              className="absolute inset-0 size-full"
            />
          </div>
        </div>
      ) : (
        // No embeddable form (YouTube @handle, an unrecognised host, or the
        // host name hasn't resolved yet on first paint) — offer the link.
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 transition-colors hover:border-primary/50"
        >
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-secondary">
            <Radio className="size-5 text-primary" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold">
              The stream is live on {label}
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              {watchUrl}
            </span>
          </span>
          <ExternalLink className="size-4 shrink-0 text-muted-foreground" />
        </a>
      )}
    </section>
  )
}
